import { GameEngine } from './engine/GameEngine.js';
import { PauseMenu } from './components/PauseMenu.js';
import { GameOverScreen } from './components/GameOverScreen.js';
import { MenuSystem } from './systems/menu.js';
import { Container } from 'pixi.js';
import { LoadingScreen } from './systems/loading.js';
import { AssetManager } from './assets/assetManager.js';
import { Player, Health, Hunger, Thirst, Wetness, Input, Velocity } from './world.js';
import { PLAYER_STATS } from './config.js';

// Game states
const GameState = {
    MENU: 'MENU',
    LOADING: 'LOADING',
    PLAYING: 'PLAYING',
    PAUSED: 'PAUSED',
    GAME_OVER: 'GAME_OVER'
};

class Game {
    constructor() {
        this.engine = null;
        this._state = GameState.MENU;
        this.pauseMenu = null;
        this.gameOverScreen = null;
        this.assetManager = new AssetManager();
        this.logState('Initial state');
    }

    get state() {
        return this._state;
    }

    set state(newState) {
        console.log(`State transition: ${this._state} -> ${newState}`);
        this._state = newState;
    }

    logState(action) {
        console.log(`[${this._state}] ${action}`);
    }

    async start() {
        try {
            // Wait for audio initialization first
            console.log('[GAME] Waiting for audio initialization...');
            await this.assetManager.audioManager.initPromise;
            
            // Load audio assets
            console.log('[GAME] Loading audio assets...');
            await this.assetManager.loadAudioAssets();
            console.log('[GAME] Audio assets loaded');

            // Start the game engine - it will handle asset loading internally
            this.engine = await GameEngine.start(this.assetManager);
            this.setupEventListeners();
            this.logState('Game started');

            // Expose the game engine to the global scope for debugging
            window.gameEngine = this.engine;
            console.log('[MAIN] Game engine exposed as window.gameEngine. Try these commands:');
            console.log('- gameEngine.placeHiveNearPlayer() - Place a hive near the player');
            console.log('- gameEngine.debugHiveStructure() - Debug hive structure detection');
            console.log('- gameEngine.debugBeeSpawning() - Test bee spawning (fixes issues)');
            console.log('- gameEngine.forceBeesMove() - Force all bees to start moving');
            console.log('- gameEngine.simulationSystem.toggleSimulation() - Toggle simulation');
            console.log('- gameEngine.simulationSystem.showAnalytics() - Show analytics panel');
            console.log('- gameEngine.simulationSystem.resetSimulation() - Reset simulation');
            console.log('- toggleSim() - Toggle simulation (shortcut)');
            console.log('- showSimAnalytics() - Show analytics panel (shortcut)');
            console.log('- resetSim() - Reset simulation (shortcut)');
            
            // Add debug command for bees
            window.testBees = () => {
                console.log("Testing bee spawning...");
                const result = this.engine.debugBeeSpawning();
                console.log(result);
                return result;
            };
            
            // Add debug command to force bee movement
            window.moveBees = () => {
                console.log("Forcing bees to move...");
                const result = this.engine.forceBeesMove();
                console.log(result);
                return result;
            };
            
            // Add debug commands for simulation
            window.toggleSim = () => {
                if (this.engine && this.engine.simulationSystem) {
                    this.engine.simulationSystem.toggleSimulation();
                    console.log("Simulation toggled");
                } else {
                    console.log("Simulation system not available");
                }
            };
            
            window.showSimAnalytics = () => {
                if (this.engine && this.engine.simulationSystem) {
                    this.engine.simulationSystem.showAnalytics();
                    console.log("Analytics panel shown");
                } else {
                    console.log("Simulation system not available");
                }
            };
            
            window.resetSim = () => {
                if (this.engine && this.engine.simulationSystem) {
                    this.engine.simulationSystem.resetSimulation();
                    console.log("Simulation reset");
                } else {
                    console.log("Simulation system not available");
                }
            };
        } catch (error) {
            console.error('[GAME] Failed to start game:', error);
            throw error;
        }
    }

    setupEventListeners() {
        // Remove any existing listeners first
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('startGame', this.handleStartGame);
        window.removeEventListener('gameOver', this.handleGameOver);

        // Create bound event handlers
        this.handleKeyDown = (e) => {
            if (e.key.toLowerCase() === 'escape' || e.key.toLowerCase() === 'p') {
                if (this.state === GameState.PLAYING) {
                    this.showPauseMenu();
                } else if (this.state === GameState.PAUSED) {
                    this.hidePauseMenu();
                } else if (this.state === GameState.GAME_OVER) {
                    this.exitToMenu();
                }
            } else if (e.key === ' ' && this.state === GameState.GAME_OVER) {
                this.restartGame();
            }
        };

        this.handleStartGame = () => {
            this.logState('Start game event received');
            this.startGame().catch(error => {
                console.error('Failed to start game:', error);
            });
        };

        this.handleGameOver = () => {
            this.logState('Game over event received');
            this.showGameOver();
        };

        // Add event listeners
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('startGame', this.handleStartGame);
        window.addEventListener('gameOver', this.handleGameOver);
    }

    showPauseMenu() {
        this.logState('Showing pause menu');
        if (!this.pauseMenu) {
            this.pauseMenu = new PauseMenu(this.engine.app);
            this.pauseMenu.onResume = () => {
                this.logState('Resume button clicked');
                this.resumeGame();
            };
            this.pauseMenu.onExit = () => {
                this.logState('Exit button clicked');
                this.exitToMenu();
            };
        }
        
        // Hide gameplay container
        this.engine.gameplayContainer.visible = false;
        
        // Show pause menu
        this.pauseMenu.show();
        
        // Update game state
        this.state = GameState.PAUSED;
        
        // Pause the game
        if (this.engine.world) {
            this.engine.world.isPaused = true;
        }
    }

    hidePauseMenu() {
        this.logState('Hiding pause menu');
        if (!this.pauseMenu) return;
        
        // Hide pause menu
        this.pauseMenu.hide();
        
        // Show gameplay container
        this.engine.gameplayContainer.visible = true;
        
        // Update game state
        this.state = GameState.PLAYING;
        
        // Unpause the game
        if (this.engine.world) {
            this.engine.world.isPaused = false;
        }
    }

    resumeGame() {
        this.logState('Resuming game');
        this.hidePauseMenu();
    }

    async exitToMenu() {
        this.logState('Exiting to menu');
        console.log('[CLEANUP] Starting menu exit cleanup');
        
        // Hide game over screen first
        if (this.gameOverScreen) {
            console.log('[CLEANUP] Cleaning up game over screen');
            this.gameOverScreen.hide();
            this.gameOverScreen.destroy();
            this.gameOverScreen = null;
        }
        
        // Hide pause menu first
        if (this.pauseMenu) {
            console.log('[CLEANUP] Cleaning up pause menu');
            this.pauseMenu.hide();
            this.pauseMenu.destroy();
            this.pauseMenu = null;
        }

        // Stop the game loop
        console.log('[CLEANUP] Stopping game loop');
        this.engine.stop();
        
        // Clean up render system via the engine instance
        if (this.engine.renderSystem && typeof this.engine.renderSystem.cleanup === 'function') {
            console.log('[CLEANUP] Cleaning up render system via GameEngine');
            this.engine.renderSystem.cleanup();
        } else {
             console.log('[CLEANUP] Render system not found on engine or cleanup method missing.');
             // If the old function was globally accessible, ensure it's not called here.
        }
        
        // Hide all game components
        console.log('[CLEANUP] Hiding game components');
        this.engine.hideGameComponents();
        
        // Hide gameplay container
        console.log('[CLEANUP] Hiding gameplay container');
        this.engine.gameplayContainer.visible = false;
        
        // Clear the world container
        if (this.engine.worldContainer) {
            console.log('[CLEANUP] Removing world container');
            this.engine.worldContainer.removeChildren();
            this.engine.gameplayContainer.removeChild(this.engine.worldContainer);
            this.engine.worldContainer = null;
        }

        // Clear the world
        if (this.engine.world) {
            console.log('[CLEANUP] Clearing world state');
            // Clear all entities and components
            this.engine.world.entities = [];
            this.engine.world.sprites = {};
            this.engine.world.selectedEntity = null;
            this.engine.world.selectedEntityType = null;
            this.engine.world.mapGenerated = false;
            this.engine.world.isPaused = true;
            this.engine.world = null;
        }

        // Reset tile manager
        console.log('[CLEANUP] Resetting tile manager');
        if (this.engine.tileManager) {
            this.engine.tileManager.reset();
        }

        // Remove UI container and system
        if (this.engine.uiContainer) {
            console.log('[CLEANUP] Removing UI container');
            this.engine.gameplayContainer.removeChild(this.engine.uiContainer);
            this.engine.uiContainer = null;
        }
        if (this.engine.uiSystem) {
            console.log('[CLEANUP] Removing UI system');
            this.engine.systems.delete('ui');
            this.engine.uiSystem = null;
        }

        // Clean up all engine systems
        console.log('[CLEANUP] Cleaning up engine systems');
        if (this.engine.systems) {
            for (const [name, system] of this.engine.systems) {
                console.log(`[CLEANUP] Removing system: ${name}`);
                if (system.destroy) {
                    system.destroy();
                }
                this.engine.systems.delete(name);
            }
        }

        // Clean up lighting system
        console.log('[CLEANUP] Cleaning up lighting system');
        if (this.engine.lightingSystem && typeof this.engine.lightingSystem.destroy === 'function') {
            this.engine.lightingSystem.destroy();
            this.engine.lightingSystem = null;
        }

        // Force a redraw of the empty world
        if (this.engine.app && this.engine.app.renderer) {
            console.log('[CLEANUP] Forcing redraw');
            this.engine.app.renderer.clear();
            this.engine.app.renderer.render(this.engine.app.stage);
        }

        // Reinitialize the menu system
        if (this.engine.menuSystem) {
            console.log('[CLEANUP] Reinitializing menu system');
            this.engine.menuSystem.destroy();
        }
        this.engine.menuSystem = new MenuSystem(this.engine.app);
        this.engine.menuSystem.show();
        
        // Reset engine state
        console.log('[CLEANUP] Resetting engine state');
        this.engine.world = null;
        this.engine.worldContainer = null;
        this.engine.uiContainer = null;
        this.engine.uiSystem = null;
        this.engine.loadingScreen = null;
        this.engine.systems = new Map();
        this.engine.renderSystem = null;
        this.engine.lightingSystem = null;
        
        this.state = GameState.MENU;
        console.log('[CLEANUP] Menu exit cleanup complete');
    }

    async startGame() {
        console.log('Starting game...');
        
        // Clean up menu first
        if (this.engine.menuSystem) {
            this.engine.menuSystem.cleanup();
            this.engine.menuSystem = null;
        }
        
        // Hide loading screen
        if (this.engine.loadingScreen) {
            this.engine.loadingScreen.hide();
        }
        
        // Start the game
        await this.engine.startGame();
        
        // Update game state
        this.state = GameState.PLAYING;
    }

    showGameOver() {
        this.logState('Showing game over screen');
        if (!this.gameOverScreen) {
            this.gameOverScreen = new GameOverScreen(this.engine.app);
        }
        
        // Hide gameplay container
        this.engine.gameplayContainer.visible = false;
        
        // Show game over screen
        this.gameOverScreen.show();
        
        // Update game state
        this.state = GameState.GAME_OVER;
        
        // Pause the game
        if (this.engine.world) {
            this.engine.world.isPaused = true;
        }
    }

    async restartGame() {
        this.logState('Restarting game');
        
        // Hide game over screen
        if (this.gameOverScreen) {
            this.gameOverScreen.hide();
            this.gameOverScreen.destroy();
            this.gameOverScreen = null;
        }
        
        // Reset world state
        if (this.engine.world) {
            this.engine.world.isGameOver = false;
            this.engine.world.isPaused = false;
        }
        
        // Show gameplay container
        this.engine.gameplayContainer.visible = true;
        
        // Update game state
        this.state = GameState.PLAYING;
        
        // Reset player stats and input
        const players = this.engine.world.entities.filter(e => Player[e]);
        if (players.length > 0) {
            const player = players[0];
            Health.value[player] = PLAYER_STATS.INITIAL_HEALTH;
            Hunger.value[player] = PLAYER_STATS.INITIAL_HUNGER;
            Thirst.value[player] = PLAYER_STATS.INITIAL_THIRST;
            Wetness.value[player] = PLAYER_STATS.INITIAL_WETNESS;
            
            // Reset input state
            Input.up[player] = 0;
            Input.down[player] = 0;
            Input.left[player] = 0;
            Input.right[player] = 0;
            
            // Reset velocity
            Velocity.x[player] = 0;
            Velocity.y[player] = 0;
        }
    }
}

// Start the game
const game = new Game();
game.start();